/* @jsx h */
import { $$ } from '../../../../shared/js/utils/dom';
import { generateRandomString } from '../../../../shared/js/utils/unique-string';
import { Shower } from '../../../../shared/js/modules/shower';
import { useRef, useEffect, useState } from 'preact/hooks';
import { useAccordionStore } from '../store/store';
import Options from './Options';
import { labels } from '../config';
import { createFocusTrap } from 'focus-trap';

let averageFilterHeight = 0;

const FilterAccordion = ({
  title,
  options,
  filterName,
  triggerAjaxSubmit,
  mode,
}) => {
  // get some props from our store
  const activeAccordions = useAccordionStore(state => state.activeAccordions);
  const addAccordion = useAccordionStore(state => state.addAccordion);
  const removeAccordion = useAccordionStore(state => state.removeAccordion);
  // check if the accordion is in the store, if yes accordion should be open by default
  const initialAccordionState = activeAccordions.includes(title);

  const [inputType, setInputType] = useState('checkbox');
  const optionsCount = options.filter(option => option.selected).length;

  const uuid = generateRandomString();
  // use useRef for shower
  const fieldsetEl = useRef(null);
  const buttonEl = useRef(null);
  const buttonPanel = useRef(null);
  const buttonApply = useRef(null);
  const buttonReset = useRef(null);

  const isSingleMode = mode === 'single';

  function handleOverlap(el) {
    const dimensions = el.getBoundingClientRect();
    const hasRightOverlap = dimensions.x + dimensions.width > window.innerWidth;
    const hasLeftOverlap = dimensions.x < 0;

    if (hasRightOverlap) {
      el.classList.add('has-right-overlap');
    }
    if (hasLeftOverlap) {
      el.classList.add('has-left-overlap');
    }
    if (hasRightOverlap || hasLeftOverlap) return;

    el.classList.remove('has-left-overlap');
    el.classList.remove('has-right-overlap');
  }

  function removeFilters() {
    const checkboxes = $$('input[type="checkbox"]', buttonPanel.current);
    checkboxes.forEach(checkbox => (checkbox.checked = false));
    triggerAjaxSubmit();
  }

  useEffect(() => {
    const handler = buttonEl.current;
    const target = buttonPanel.current;
    const fieldset = fieldsetEl.current;
    const onKeydown = e => {
      if (e.key === 'Escape') {
        showerInstance.close();
        handler.focus();
      }
    };
    let showerInstance;
    let focusTrapInstance;
    const addFocusTrap = el => {
      focusTrapInstance = createFocusTrap(el, {
        clickOutsideDeactivates: true,
      });
      focusTrapInstance.activate();
    };
    const removeFocusTrap = () => {
      focusTrapInstance?.deactivate();
    };
    const outsideClickHandler = e => {
      let isOutsideClick = true;
      if (showerInstance.el.parentNode.contains(e.target))
        isOutsideClick = false;
      if (isOutsideClick && showerInstance.show) showerInstance.close();
    };

    // Triggers a class for an alternative filter layout if filter show breaking text.
    if (averageFilterHeight === 0) averageFilterHeight = handler.clientHeight;
    if (averageFilterHeight !== handler.clientHeight)
      fieldsetEl.current.parentNode.classList.add('has-breaking-filters');

    if (handler && target) {
      // init accordion functionality
      showerInstance = new Shower({
        target,
        handler,
        easing: 'easeInOutQuart',
        duration: 300,
        initOpened: isSingleMode || initialAccordionState,
        openClassName: 'is-open',
        toggleStart: (_, state) => {
          if (state === 'show') {
            handleOverlap(target);
            addFocusTrap(fieldset);
          }
          if (state !== 'show') {
            removeFocusTrap();
          }
        },
        toggleEnd: (_, state) => {
          /* when ajax gets triggered and the all the filter gets replaced, preact re-init this whole component
           * so the open accordion will always be close again, which is annoying.
           * to prevent that, we're using the store "Zustand" to keep the accordion state persistent
           */
          if (state === 'show') {
            addAccordion(title);
          } else {
            removeAccordion(title);
          }

          if (state !== 'show') {
            handleOverlap(target);
          }
        },
      });
      showerInstance.init();

      handler.addEventListener('keydown', onKeydown);
      target.addEventListener('keydown', onKeydown);
      buttonApply.current.addEventListener('click', showerInstance.close);
      buttonReset.current.addEventListener('click', showerInstance.close);
    }

    if (showerInstance) {
      window.addEventListener('click', outsideClickHandler);
    }
  }, []);

  useEffect(() => {
    const type = isSingleMode ? 'radio' : 'checkbox';
    setInputType(type);
  }, [isSingleMode]);

  return (
    <fieldset className="search-fieldset" ref={fieldsetEl}>
      <legend class="search-fieldset__legend">
        <button
          className="search-fieldset__button"
          aria-controls={`search-fieldset-${uuid}`}
          ref={buttonEl}
          type="button"
        >
          <span>
            {title}
            {optionsCount > 0 && (
              <span className="search-fieldset__options-counter">
                {optionsCount}
                <span class="sr-only">{labels.faceting.resultsNarrowedBy}</span>
              </span>
            )}
          </span>
          <span class="icon icon--chevron-down">
            <svg
              class="icon__svg"
              focusable="false"
              role="img"
              aria-hidden="true"
            >
              <use href="#icon-chevron-down" />
            </svg>
            <span class="sr-only">{labels.faceting.openFilters}</span>
          </span>
          <span class="icon icon--chevron-up">
            <svg
              class="icon__svg"
              focusable="false"
              role="img"
              aria-hidden="true"
            >
              <use href="#icon-chevron-up" />
            </svg>
            <span class="sr-only">{labels.faceting.closeFilters}</span>
          </span>
        </button>
      </legend>
      <div
        id={`search-fieldset-${uuid}`}
        className="search-fieldset__panel"
        ref={buttonPanel}
      >
        <div className={`search-fieldset__panel-inner`}>
          <Options
            options={options}
            inputType={inputType}
            filterName={filterName}
          />
          <div class="search-fieldset__controls">
            <button
              ref={buttonReset}
              class="search-fieldset__control search-fieldset__control--reset"
              type="button"
              onClick={removeFilters}
            >
              {labels.faceting.removeFilters}
              <span class="icon icon--cross">
                <svg
                  class="icon__svg"
                  focusable="false"
                  role="img"
                  aria-hidden="true"
                >
                  <use href="#icon-cross" />
                </svg>
              </span>
            </button>
            <button
              ref={buttonApply}
              class="search-fieldset__control search-fieldset__control--apply"
              type="button"
              onClick={triggerAjaxSubmit}
            >
              {labels.faceting.applyFilters}
              <span class="icon icon--chevron-right">
                <svg
                  class="icon__svg"
                  focusable="false"
                  role="img"
                  aria-hidden="true"
                >
                  <use href="#icon-chevron-right" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </fieldset>
  );
};

export default FilterAccordion;
